import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { SearchBar } from "@/components/SearchBar";

const SLIDES = [
  { image: "https://images.pexels.com/photos/14568458/pexels-photo-14568458.jpeg", label: "Kerala, India" },
  { image: "https://images.pexels.com/photos/25252018/pexels-photo-25252018.jpeg", label: "The Himalayas" },
  { image: "https://images.pexels.com/photos/9400976/pexels-photo-9400976.jpeg", label: "Maldives" },
];

const Line = ({ children, delay }) => (
  <span className="masked-line">
    <motion.span initial={{ y: "110%" }} animate={{ y: 0 }}
      transition={{ delay, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const [idx, setIdx] = useState(0);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 160]);
  const scale = useTransform(scrollY, [0, 600], [1, 1.12]);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="relative h-[100svh] min-h-[640px] overflow-hidden bg-ink" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <AnimatePresence mode="sync">
          <motion.img key={idx} src={SLIDES[idx].image} alt={`${SLIDES[idx].label} travel destination`}
            initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: "easeOut" }}
            className="absolute inset-0 w-full h-full object-cover" />
        </AnimatePresence>
      </motion.div>
      <div className="absolute inset-0 bg-black/40" />

      <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 flex flex-col justify-center">
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }}
          className="text-bone/70 text-xs uppercase tracking-[0.3em] mb-6" data-testid="hero-overline">
          Click2trip — We Exchange Happiness · Since 2011
        </motion.p>
        <h1 className="font-display text-bone text-5xl sm:text-6xl font-black leading-[1.02] max-w-3xl" data-testid="hero-headline">
          <Line delay={0.35}>The world is</Line>
          <Line delay={0.5}>one <span className="text-sun">click</span> away.</Line>
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-6 text-bone/80 text-base sm:text-lg font-light max-w-xl" data-testid="hero-subtext">
          Domestic escapes, international adventures and honeymoons crafted by humans — with verified stays and a trip manager on WhatsApp, 24/7.
        </motion.p>
        <div className="mt-10">
          <SearchBar />
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
          className="mt-8 flex items-center gap-4">
          {SLIDES.map((s, i) => (
            <button key={s.label} onClick={() => setIdx(i)} data-testid={`hero-slide-${i}`}
              className="group flex items-center gap-2">
              <span className={`h-0.5 rounded-full transition-all duration-500 ${i === idx ? "w-10 bg-sun" : "w-5 bg-bone/30 group-hover:bg-bone/60"}`} />
              <span className={`text-xs uppercase tracking-widest transition-colors ${i === idx ? "text-bone" : "text-bone/40 group-hover:text-bone/70"}`}>{s.label}</span>
            </button>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
